/**
 * Design tokens — single source of truth for the app's visual language.
 *
 * Direction: "Linear/Vercel" — a neutral zinc scale, hairline borders instead of
 * drop shadows, one accent colour, dense typography.
 */

export const accent = {
	base: "#5e6ad2",
	hover: "#4f5bc4",
	active: "#4450b0",
	soft: "#eef0fb",
	softDark: "#20233a",
	onDark: "#7c86e8",
} as const;

/** Neutral scale (zinc). */
export const zinc = {
	50: "#fafafa",
	100: "#f4f4f5",
	200: "#e4e4e7",
	300: "#d4d4d8",
	400: "#a1a1aa",
	500: "#71717a",
	600: "#52525b",
	700: "#3f3f46",
	800: "#27272a",
	900: "#18181b",
	950: "#09090b",
} as const;

export const status = {
	success: "#16a34a",
	warning: "#d97706",
	error: "#dc2626",
	info: accent.base,
} as const;

export const light = {
	/** App canvas behind the surfaces. */
	canvas: zinc[50],
	/** Cards, tables, modals. */
	surface: "#ffffff",
	/** Sidebar / header chrome — subtly off-surface. */
	chrome: "#ffffff",
	border: zinc[200],
	borderSubtle: zinc[100],
	text: zinc[900],
	textSecondary: zinc[600],
	textMuted: zinc[400],
	hover: zinc[100],
	accent: accent.base,
	accentSoft: accent.soft,
} as const;

export const dark = {
	// Navy-tinted rather than neutral gray, and surfaces sit clearly above the
	// page background — that layering reads as "light" even in dark mode,
	// instead of the flat, high-contrast black/white of a pure zinc scale.
	canvas: "#0c0c17",
	surface: "#16162a",
	chrome: "#121222",
	border: "#28283f",
	borderSubtle: "#1e1e33",
	text: "#e6e8f0",
	textSecondary: "#9a9fb8",
	textMuted: "#666a80",
	hover: "#1f1f38",
	accent: accent.onDark,
	accentSoft: "#22223f",
} as const;

export const radius = {
	sm: 6,
	base: 8,
	lg: 12,
	xl: 16,
} as const;

export const fontFamily =
	'Inter, "Inter var", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export const fontFamilyMono =
	'"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';
